import { Download, KeyRound, ListChecks, Lock, Mail, Phone, SlidersHorizontal, type LucideIconData } from 'lucide-angular';
import type { AppTranslations } from '../../core/interfaces/app-translations.interface';

/** Une ligne du menu du compte (avatar) : ouvre une vue de /settings ou une route. */
export interface ItemMenuCompte {
    id: 'todos' | 'config' | 'pin' | 'verrou' | 'calls' | 'email' | 'installer';
    icone: LucideIconData;
    /** Classes de couleur de l'icone, claires puis sombres. */
    couleur: string;
    libelle: string;
    vue?: 'todos' | 'config' | 'pin';
    route?: string;
    /** Action sans destination : verrouiller le poste, ou installer l'application. */
    action?: 'verrouiller' | 'installer';
    /** Pastille a droite (« NOUVEAU »). */
    pastille?: string;
}

/**
 * Lignes du menu du compte, dans l'ordre.
 *
 * « Installer l'application » ferme la liste quand l'installation est possible (maquette « Installer
 * l'application POS »). « Verrouiller le poste » suit « Code NIP » dans les deux menus (maquette `Menu-SansVente-Compte`).
 * Menu de vente : l'ordre historique. Menu « sans vente » (maquette
 * `Menu-SansVente-Compte`) : Taches, Appels, Configuration, Code PIN — le courriel n'y
 * figure pas, il est dans la navigation du bas. La deconnexion, separee, reste dans le
 * gabarit.
 */
export function itemsMenuCompte(vente: boolean, t: AppTranslations, installable = false): ItemMenuCompte[] {
    const taches: ItemMenuCompte = { id: 'todos', icone: ListChecks, couleur: 'text-primary-600 dark:text-primary-400', libelle: t.settings.todos, vue: 'todos' };
    const config: ItemMenuCompte = { id: 'config', icone: SlidersHorizontal, couleur: 'text-accent-600 dark:text-accent-400', libelle: t.settings.config, vue: 'config' };
    const pin: ItemMenuCompte = { id: 'pin', icone: KeyRound, couleur: 'text-success-600 dark:text-success-400', libelle: t.settings.pin_title, vue: 'pin' };
    const verrou: ItemMenuCompte = { id: 'verrou', icone: Lock, couleur: 'text-primary-600 dark:text-primary-400', libelle: t.verrou.button, action: 'verrouiller', pastille: t.verrou.badge_new };
    const appels: ItemMenuCompte = { id: 'calls', icone: Phone, couleur: 'text-primary-600 dark:text-primary-400', libelle: t.nav.calls || 'Calls', route: '/calls' };
    const courriel: ItemMenuCompte = { id: 'email', icone: Mail, couleur: 'text-blue-600 dark:text-blue-400', libelle: t.nav.email, route: '/email' };
    const lignes = vente ? [taches, config, pin, verrou, appels, courriel] : [taches, appels, config, pin, verrou];
    // « Installer l'application » : derniere ligne avant la deconnexion, seulement quand l'installation est possible.
    if (installable) lignes.push({ id: 'installer', icone: Download, couleur: 'text-primary-600 dark:text-primary-400', libelle: t.pwa.install_button, action: 'installer' });
    return lignes;
}

/** Habillage du menu du compte : classes du panneau, des lignes, du voile. */
export interface HabillageMenuCompte {
    panneau: string;
    voile: string;
    ligne: string;
    libelle: string;
    tailleIcone: number;
    /** Vrai : l'icone prend la couleur propre a sa ligne ; faux : celle du texte. */
    iconeColoree: boolean;
    /** Classes ajoutees a la ligne « Verrouiller le poste » (mise en avant de la maquette). */
    ligneVerrou: string;
    deconnexion: string;
    libelleDeconnexion: string;
    iconeDeconnexion: string;
    /** Filet entre les lignes et la deconnexion. */
    filet: boolean;
}

const HABILLAGE_VENTE: HabillageMenuCompte = {
    panneau: 'absolute top-full right-0 mt-2 w-52 bg-white dark:bg-surface-800 rounded-xl shadow-xl border border-surface-100 dark:border-surface-700 overflow-hidden py-1 z-50 animate-in fade-in zoom-in-95 duration-200',
    voile: 'fixed inset-0 z-40 bg-transparent',
    ligne: 'w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors',
    libelle: 'text-sm font-medium text-surface-700 dark:text-surface-200',
    tailleIcone: 16,
    iconeColoree: true,
    ligneVerrou: '',
    deconnexion: 'w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-danger-50 dark:hover:bg-danger-900/30 transition-colors',
    libelleDeconnexion: 'text-sm font-medium text-danger-600 dark:text-danger-400',
    iconeDeconnexion: 'text-danger-600 dark:text-danger-400',
    filet: true,
};

/**
 * Maquette `Menu-SansVente-Compte` : panneau de 270 px, lignes de 48 px aux coins
 * arrondis, icones de 20 px de la couleur du texte, deconnexion comme les autres
 * lignes, voile sombre derriere.
 */
const HABILLAGE_RH: HabillageMenuCompte = {
    panneau: 'absolute top-full right-0 mt-2 w-[270px] p-2 flex flex-col gap-0.5 bg-white dark:bg-surface-900 rounded-2xl shadow-xl border border-surface-200 dark:border-surface-800 z-50',
    voile: 'fixed inset-0 z-40 bg-slate-950/45',
    ligne: 'w-full min-h-12 px-3.5 rounded-xl text-left flex items-center gap-3 font-medium text-surface-900 dark:text-white hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors',
    libelle: '',
    tailleIcone: 20,
    iconeColoree: false,
    ligneVerrou: 'bg-primary-50 dark:bg-primary-900/30 !text-primary-700 dark:!text-primary-300 font-bold',
    deconnexion: 'w-full min-h-12 px-3.5 rounded-xl text-left flex items-center gap-3 font-medium text-surface-900 dark:text-white hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors',
    libelleDeconnexion: '',
    iconeDeconnexion: '',
    filet: false,
};

export const habillageMenuCompte = (vente: boolean): HabillageMenuCompte => (vente ? HABILLAGE_VENTE : HABILLAGE_RH);
